import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  Clock, 
  Users, 
  Calendar,
  ArrowRight,
  Settings,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useLifely } from '../context/LifelyContext';

const ContinuityOverview = () => {
  const { continuityConfig, fetchContinuity, isAuthenticated, loading } = useLifely();

  // Fetch continuity data when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchContinuity();
    }
  }, [isAuthenticated, fetchContinuity]);

  // Handle null config gracefully
  const config = continuityConfig || {};
  const { 
    isConfigured = false, 
    checkInFrequency = 3, 
    reminderCount = 0, 
    nominees = [], 
    lastCheckIn = null, 
    nextCheckIn = null 
  } = config;

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Show loading state
  if (loading.continuity) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="continuity-overview-page">
      {/* Header */}
      <section className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground">
          Continuity
        </h1>
        <p className="text-muted-foreground text-base max-w-2xl">
          A simple system to check in periodically and share access with people you trust if you stop responding. 
          This does not assume anything has happened—it only follows your configured rules.
        </p>
      </section>

      {/* Status Overview */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="continuity-status">
        {/* Configuration Status */}
        <Card className={`border-2 ${isConfigured ? 'border-green-500/30 bg-green-500/5' : 'border-blue-500/30 bg-blue-500/5'}`} data-testid="config-status-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              {isConfigured ? (
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className={`font-medium ${isConfigured ? 'text-green-700 dark:text-green-400' : 'text-blue-700 dark:text-blue-400'}`}>
                  {isConfigured ? 'Active' : 'Not configured'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Last Check-in */}
        <Card className="border border-border" data-testid="last-checkin-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <Calendar className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Check-in</p>
                <p className="font-medium text-foreground">
                  {formatDate(lastCheckIn)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Check-in */}
        <Card className={`border ${!isConfigured ? 'border-blue-500/30 bg-blue-500/5' : 'border-border'}`} data-testid="next-checkin-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${!isConfigured ? 'bg-blue-500/20' : 'bg-accent/20'}`}>
                <Clock className={`w-5 h-5 ${!isConfigured ? 'text-blue-600' : 'text-accent'}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Next Check-in</p>
                <p className={`font-medium ${!isConfigured ? 'text-blue-700 dark:text-blue-400' : 'text-foreground'}`}>
                  {isConfigured ? formatDate(nextCheckIn) : 'Not Active'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nominees */}
        <Card className="border border-border" data-testid="nominees-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <Users className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Nominees</p>
                <p className="font-medium text-foreground">
                  {nominees.length} {nominees.length === 1 ? 'person' : 'people'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Current Configuration Summary */}
      {isConfigured && (
        <Card className="border border-border" data-testid="config-summary">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium font-body">
              Current Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Check-in frequency</p>
                <p className="font-medium">Every {checkInFrequency} months</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Reminder attempts</p>
                <p className="font-medium">{reminderCount} reminders before trigger</p>
              </div>
            </div>
            
            {nominees.length > 0 && (
              <div className="pt-3 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">Nominated people</p>
                <div className="space-y-2">
                  {nominees.map((nominee) => (
                    <div key={nominee.id} className="flex items-center gap-2 text-sm">
                      <span className="font-medium">{nominee.name}</span>
                      <span className="text-muted-foreground">({nominee.role})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Card */}
      <Card className="border border-border bg-muted/30" data-testid="setup-cta">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <h3 className="font-heading font-medium text-lg">
                {isConfigured ? 'Update Your Settings' : 'Set Up Continuity'}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                {isConfigured 
                  ? 'Review and adjust your check-in preferences, nominees, and access scope.'
                  : 'Configure how often you check in and who can access your maps if you stop responding.'
                }
              </p>
            </div>
            <Link to="/continuity/setup" data-testid="setup-continuity-btn">
              <Button className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                {isConfigured ? 'Edit Settings' : 'Set Up Now'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Info Note */}
      <section>
        <div className="bg-muted/50 rounded-xl p-6 border border-border">
          <p className="text-sm text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">How it works:</span> Lifely sends you a simple check-in request at your chosen frequency. 
            If you don&apos;t respond after the configured number of reminders, Lifely follows your trigger preferences—which might be to do nothing, 
            notify someone that Lifely exists, or invite them to view your maps. Not responding does not confirm anything has happened.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContinuityOverview;
